import { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';

const toDateInput = (date) => date.toISOString().slice(0, 10);

const defaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from: toDateInput(from), to: toDateInput(to) };
};

// Every figure comes from GET /ga/metrics/platform?from=&to= — the
// date-range controls only change the request's query params, never filter
// an already-fetched set client-side (CHANGES.md decision 4).
const useGAReportingPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const initialRange = defaultRange();
  const [fromInput, setFromInput] = useState(initialRange.from);
  const [toInput, setToInput] = useState(initialRange.to);
  const [range, setRange] = useState(initialRange);

  const [status, setStatus] = useState('loading');
  const [errorInfo, setErrorInfo] = useState(null);
  const [data, setData] = useState(null);

  const fetchMetrics = useCallback(() => {
    if (!config?.backendUrl) return;
    setStatus('loading');
    const params = {
      from: `${range.from}T00:00:00`,
      to: `${range.to}T23:59:59`,
    };
    axios.get(`${config.backendUrl}/ga/metrics/platform`, { params })
      .then((res) => {
        setData(res.data);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching GA platform metrics', error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /ga/metrics/platform · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      });
  }, [config, range, t]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const runReport = () => setRange({ from: fromInput, to: toInput });

  const effectiveStatus = previewState || status;

  return {
    t, effectiveStatus, data, errorInfo, fetchMetrics,
    fromInput, setFromInput, toInput, setToInput, runReport,
  };
};

export default useGAReportingPage;
